// contexts/SocketContext.js - Updated to work with backend aggregation and cache invalidation events
import React, {
  useState,
  createContext,
  useContext,
  useEffect,
} from "react";
import { io } from "socket.io-client";
import { getWebSocketEndpoint } from "@/webSocketDefaults.js";
import { getTenant } from "@/TENANT_CONFIGS";
import { useSubscribeToQueryData } from "@/utils/react-query-hooks/general";
import { useQueryClient } from "react-query";
import {
  updateAllTplsCache,
  updateRolesConfigCacheForNewlyCreatedTpl,
} from "@/utils/react-query-hooks/tci";

export const SocketContext = createContext();

export const SocketContextProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // These are now directly set from backend aggregation
  const [segByImportIdJobs, setSegByImportIdJobs] = useState(undefined);
  const [importStatusByContentType, setImportStatusByContentType] = useState({});

  // Auto-annotate (parallel channel — same shape as import, different activity id)
  const [segByAutoAnnotateIdJobs, setSegByAutoAnnotateIdJobs] = useState(undefined);
  const [autoAnnotateStatusByContentType, setAutoAnnotateStatusByContentType] = useState({});

  // Tag-sync (rename/delete propagation through TAG_SYNC_PLAN/BATCH queues,
  // grouped by tagSyncId). tagSyncStatusByTagId is the quick lookup
  // TagsManagerV2 uses to disable editing on tags whose propagation is still
  // running — including right after a page refresh (current-progress rehydrates it).
  const [segByTagSyncIdJobs, setSegByTagSyncIdJobs] = useState(undefined);
  const [tagSyncStatusByTagId, setTagSyncStatusByTagId] = useState({});

  // Field migration (in-place type change → per-doc value migration through the
  // same TAG_SYNC_PLAN/BATCH queues, grouped by fieldMigrationId).
  // fieldMigrationStatusByValuePath is the quick lookup (`${contentType}:${newValuePath}`
  // → "fieldMigrating" | "fieldMigrationCompleted") the SM listing table uses to
  // swap a migrating column to its "Updating data…" placeholder — including
  // right after a page refresh (current-progress rehydrates it).
  const [segByFieldMigrationIdJobs, setSegByFieldMigrationIdJobs] = useState(undefined);
  const [fieldMigrationStatusByValuePath, setFieldMigrationStatusByValuePath] = useState({});
  
  // console.log({segByImportIdJobs, importStatusByContentType})
  
  const queryClient = useQueryClient();
  const { data: userData } = useSubscribeToQueryData("userData");
  const tenant = getTenant();
  const SOCKET_IO_ENDPOINT = getWebSocketEndpoint();

  useEffect(() => {
    const xAuthToken = localStorage.token;
    const socketInstance = io(SOCKET_IO_ENDPOINT, {
      transports: ["polling"],
      auth: {
        token: xAuthToken,
        tenant: tenant,
      },
    });

    socketInstance.on("connect", () => {
      setIsConnected(true);
      setSocket(socketInstance);
    });

    socketInstance.on("disconnect", () => {
      setIsConnected(false);
    });

    // Handle aggregated progress updates from backend
    socketInstance.on("progress-update", (aggregatedData) => {
      console.log("Received progress update:", aggregatedData);

      setSegByImportIdJobs(aggregatedData.segByImportIdJobs);
      setImportStatusByContentType(aggregatedData.importStatusByContentType);
      setSegByAutoAnnotateIdJobs(aggregatedData.segByAutoAnnotateIdJobs);
      setAutoAnnotateStatusByContentType(aggregatedData.autoAnnotateStatusByContentType || {});
      setSegByTagSyncIdJobs(aggregatedData.segByTagSyncIdJobs);
      setTagSyncStatusByTagId(aggregatedData.tagSyncStatusByTagId || {});
      setSegByFieldMigrationIdJobs(aggregatedData.segByFieldMigrationIdJobs);
      setFieldMigrationStatusByValuePath(aggregatedData.fieldMigrationStatusByValuePath || {});
    });

    // Handle initial progress data when connecting
    socketInstance.on("current-progress", (progressData) => {
      console.log("Received current progress:", progressData);

      setSegByImportIdJobs(progressData.segByImportIdJobs);
      setImportStatusByContentType(progressData.importStatusByContentType);
      setSegByAutoAnnotateIdJobs(progressData.segByAutoAnnotateIdJobs);
      setAutoAnnotateStatusByContentType(progressData.autoAnnotateStatusByContentType || {});
      setSegByTagSyncIdJobs(progressData.segByTagSyncIdJobs);
      setTagSyncStatusByTagId(progressData.tagSyncStatusByTagId || {});
      setSegByFieldMigrationIdJobs(progressData.segByFieldMigrationIdJobs);
      setFieldMigrationStatusByValuePath(progressData.fieldMigrationStatusByValuePath || {});
    });

    // Handle immediate cache invalidation for direct import completion
    socketInstance.on("cache-invalidation", async (data) => {
      console.log("Received cache invalidation:", data);

      const {
        contentType,
        tpl,
        updatedRolesConfig,
        deletedPendingDirectDataImportId,
      } = data;

      if (tpl) {
        // A getAllTpls fetch issued BEFORE this tpl was committed can still be
        // in flight; if it resolves after the setQueryData below, it overwrites
        // our patch with a list that predates the tpl and the row vanishes
        // until a manual refresh. Cancel first so the stale response is
        // discarded. Awaited deliberately: cancel() dispatches a revert to the
        // pre-fetch state, and that revert has to land BEFORE we write, not
        // after. Nothing is lost - the pushed tpl is fresher than the response
        // being dropped, and the next natural refetch reconciles the rest.
        //
        // Only cancel a query that ALREADY HAS DATA. Cancelling a cold fetch
        // reverts it to `data: undefined` and react-query does not refire it,
        // so updateAllTplsCache would then skip the patch (its own
        // existing-data guard) and the query would sit in `loading` until a
        // window focus or remount — a stuck skeleton, which is worse than the
        // missing row we're fixing here. With nothing cached, the in-flight
        // fetch is the only delivery path, so let it land.
        for (const queryKey of [["tci", "tpls"], ["allTpls"]]) {
          if (queryClient.getQueryData(queryKey) !== undefined) {
            await queryClient.cancelQueries(queryKey);
          }
        }

        updateAllTplsCache({
          queryClient,
          updatedTpl: tpl,
        });
      }

      if (updatedRolesConfig) {
        updateRolesConfigCacheForNewlyCreatedTpl({
          updatedRolesConfig,
          queryClient,
        });
      }

      // A resumed draft was consumed — deleted on the BE inside the same
      // transaction that created the tpl. Surgically drop its "Pending Import"
      // row from the datasets table; the replacing dataset row arrives via the
      // tpl cache update above. Filter is idempotent, so the duplicate emit
      // from the job-completed path (after the mid-flight tplCreated one) is
      // harmless.
      if (deletedPendingDirectDataImportId) {
        queryClient.setQueryData(["pendingDirectDataImports"], (old) =>
          old
            ? old.filter((p) => p._id !== deletedPendingDirectDataImportId)
            : old
        );
      }

      queryClient.invalidateQueries({
        queryKey: [
          "contentListing",
          "lazy",
          [contentType],
        ],
      });

      // Also invalidate every externalConnection query — if this tpl was
      // created via an Integrations Activate flow, the matching connection's
      // contentType has just been written by markIntegrationActivated. Refetch
      // so the Integrations card / modal flip from "Connected — setup needed"
      // to "Active" without waiting for window focus or manual refresh.
      // Broad invalidate is cheap here (a handful of cards at most).
      queryClient.invalidateQueries({ queryKey: ["externalConnection"] });
    });

    return () => {
      socketInstance.disconnect();
    };
  }, [userData, queryClient]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        segByImportIdJobs, // Now set directly from backend
        importStatusByContentType, // Now set directly from backend
        segByAutoAnnotateIdJobs,
        autoAnnotateStatusByContentType,
        segByTagSyncIdJobs,
        tagSyncStatusByTagId,
        segByFieldMigrationIdJobs,
        fieldMigrationStatusByValuePath,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => useContext(SocketContext);